/**
 * CompressorControls Component
 *
 * Specialized control panel for the Compressor effect.
 * Displays 6 rotary knobs, knee type selector, detection mode selector,
 * and a real-time gain reduction meter.
 */
import { useMemo } from 'react';
import { RotaryKnob } from '../../ui/RotaryKnob';
import { GainReductionMeter } from './GainReductionMeter';
import { PresetSelector } from './PresetSelector';
import type { EffectInstance, EffectDefinition } from '../../../services/effects/EffectsManager';
import { getParameterDescription } from '../../../constants/effectDescriptions';
import './CompressorControls.css';
/**
 * Props for CompressorControls component
 */
export interface CompressorControlsProps {
    /** Effect instance */
    effect: EffectInstance;
    /** Effect definition */
    effectDefinition: EffectDefinition;
    /** Callback when parameter changes */
    onParameterChange: (parameterId: string, value: number) => void;
    /** Callback when multiple parameters change at once (for presets) */
    onParametersChange?: ((parameters: Record<string, number>) => void) | undefined;
    /** Whether controls are disabled */
    disabled?: boolean;
}
/**
 * CompressorControls component
 *
 * Layout:
 * - Top row: 3 knobs (threshold, ratio, attack)
 * - Middle row: 3 knobs (release, makeup, mix)
 * - Bottom row: Knee type selector, detection mode selector
 * - Right side: Gain reduction meter
 *
 * @param props - Component props
 * @returns CompressorControls component
 */
export function CompressorControls({ effect, effectDefinition, onParameterChange, onParametersChange, disabled = false, }: CompressorControlsProps) {
    /**
     * Get parameter definition by ID
     */
    const getParamDef = (id: string) => {
        return effectDefinition.parameters.find(p => p.id === id);
    };
    /**
     * Get parameter value
     */
    const getParamValue = (id: string) => {
        const paramDef = getParamDef(id);
        return effect.parameters[id] ?? paramDef?.default ?? 0;
    };
    /**
     * Calculate current gain reduction (simulated for display)
     * In a real implementation, this would come from the audio processor
     */
    const gainReduction = useMemo(() => {
        const threshold = getParamValue('threshold');
        const ratio = getParamValue('ratio');
        // Simulate gain reduction based on threshold and ratio
        // This is a simplified calculation for visualization
        const inputLevel = -10; // Assume typical input level
        if (inputLevel > threshold) {
            const excess = inputLevel - threshold;
            const reduction = excess * (1 - 1 / ratio);
            return -reduction;
        }
        return 0;
    }, [effect.parameters]);
    /**
     * Handle preset selection
     */
    const handlePresetSelect = (presetParameters: Record<string, number>) => {
        console.log('Applying preset parameters:', presetParameters);
        // Use batch update if available, otherwise fall back to individual updates
        if (onParametersChange) {
            onParametersChange(presetParameters);
        }
        else {
            // Fallback: Apply parameters individually with delays
            Object.entries(presetParameters).forEach(([paramId, value], index) => {
                setTimeout(() => {
                    onParameterChange(paramId, value);
                }, index * 10);
            });
        }
    };
    return (<div className="compressor-controls">
      <div className="compressor-controls-main">
        {/* Preset Selector */}
        <PresetSelector effectType={effect.type} onPresetSelect={handlePresetSelect} disabled={disabled}/>

        {/* Top row: Threshold, Ratio, Attack */}
        <div className="compressor-controls-row">
          {['threshold', 'ratio', 'attack'].map((paramId) => {
            const paramDef = getParamDef(paramId);
            if (!paramDef)
                return null;
            const value = getParamValue(paramId);
            const tooltip = getParameterDescription(effect.type, paramId);
            return (<RotaryKnob key={paramId} value={value} min={paramDef.min} max={paramDef.max} default={paramDef.default} step={paramDef.step} label={paramDef.name} {...(paramDef.unit && { unit: paramDef.unit })} onChange={(val) => onParameterChange(paramId, val)} disabled={disabled} size="medium" {...(tooltip && { tooltip })}/>);
        })}
        </div>

        {/* Bottom row: Release, Makeup, Mix */}
        <div className="compressor-controls-row">
          {['release', 'makeup', 'mix'].map((paramId) => {
            const paramDef = getParamDef(paramId);
            if (!paramDef)
                return null;
            const value = getParamValue(paramId);
            const tooltip = getParameterDescription(effect.type, paramId);
            return (<RotaryKnob key={paramId} value={value} min={paramDef.min} max={paramDef.max} default={paramDef.default} step={paramDef.step} label={paramDef.name} {...(paramDef.unit && { unit: paramDef.unit })} onChange={(val) => onParameterChange(paramId, val)} disabled={disabled} size="medium" {...(tooltip && { tooltip })}/>);
        })}
        </div>
      </div>

      {/* Gain Reduction Meter */}
      <div className="compressor-controls-meter">
        <GainReductionMeter gainReduction={gainReduction}/>
      </div>
    </div>);
}
